import { Listbox } from "@headlessui/react";
import {
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";

type PagerProps = {
  page: number;
  pageSize?: number;
  pageOptions?: number[];
  total: number;
  showArrows?: boolean;
  showDoubleArrows?: boolean;
  onChange: (page: number, pageSize: number) => void;
};

const Pager = (props: PagerProps) => {
  const page = props.page;
  const pageSize = props.pageSize ?? 1;
  const total = props.total;
  const pageOptions = props.pageOptions;
  const showArrows = props.showArrows;
  const showDoubleArrows = props.showDoubleArrows;
  const onChange = props.onChange;
  const numberOfPages =
    Math.floor(total / pageSize ?? 1) + (total % pageSize > 0 ? 1 : 0);

  var buttonsToShow = [];
  var start = 0;
  if (page - 4 >= 0) {
    start = page - 4;
  }
  for (var i = 0; i <= 4; i++) {
    if (start + i <= numberOfPages - 1) {
      buttonsToShow.push(start + i + 1);
    }
  }
  if (buttonsToShow[0] !== 1) {
    // shifted past first page, add ...
    buttonsToShow = [-1, ...buttonsToShow];
  }

  if (
    buttonsToShow[buttonsToShow.length - 1] !== numberOfPages &&
    numberOfPages !== 1
  ) {
    buttonsToShow.push(-1);
  }
  const canNextPage = page + 1 <= numberOfPages - 1 ? true : false;
  const canPreviousPage = page - 1 >= 0 ? true : false;
  const nextPage = () => {
    if (canNextPage) {
      onChange(page + 1, pageSize);
    }
  };
  const prevPage = () => {
    if (canPreviousPage) {
      onChange(page - 1, pageSize);
    }
  };
  const jumpToPage = (pageValue: number) => {
    onChange(pageValue, pageSize);
  };
  const jumpToStart = () => {
    if (canPreviousPage) {
      onChange(0, pageSize);
    }
  };
  const jumpToEnd = () => {
    if (canNextPage) {
      onChange(numberOfPages - 1, pageSize);
    }
  };

  const pageSizeChanged = (newSize: number) => {
    onChange(0, newSize);
  };

  return (
    <div className="w-full px-16 mb-12">
      <div className="grid sm:grid-cols-2 md:grid-cols-3 py-8 items-center border-t">
        {props.pageOptions && (
          <div className="flex justify-center items-center md:justify-start flex-grow">
            <div className="relative">
              <Listbox
                value={pageSize}
                onChange={(e) => {
                  pageSizeChanged(e);
                }}
              >
                <Listbox.Button className="py-2 px-4 rounded-md border border-primary-blue mx-2 bg-white min-w-64 flex items-center justify-between">
                  <div>{pageSize}</div>
                  <ChevronDownIcon className="h-4 w-4 ml-4" />
                </Listbox.Button>
                <Listbox.Options className="absolute w-full overflow-y-auto ml-2 mt-1 bg-white rounded-md z-10 shadow-lg">
                  {pageOptions &&
                    pageOptions.map((option) => (
                      <Listbox.Option
                        key={`po-${option}`}
                        value={option}
                        className="p-2 uppercase cursor-pointer"
                      >
                        {option}
                      </Listbox.Option>
                    ))}
                </Listbox.Options>
              </Listbox>
            </div>{" "}
            Per Page
          </div>
        )}
        {!props.pageOptions && <div></div>}
        <ul className="flex-grow mt-4 sm:mt-0 flex justify-center items-center">
          {showDoubleArrows && (
            <li>
              <button
                className={`rounded-full h-12 w-12 flex items-center justify-center mx-1 ${
                  canPreviousPage ? "" : "cursor-not-allowed"
                }`}
                onClick={(e) => {
                  jumpToStart();
                }}
              >
                <ChevronDoubleLeftIcon
                  className={`h-8 w-8 ${
                    canPreviousPage ? "text-primary-blue" : "text-gray-300"
                  }`}
                />
              </button>
            </li>
          )}
          {showArrows && (
            <li>
              <button
                className={`rounded-full h-12 w-12 flex items-center justiyf-center mx-1 ${
                  canPreviousPage ? "" : "cursor-not-allowed"
                }`}
                onClick={(e) => {
                  prevPage();
                }}
              >
                <ChevronLeftIcon
                  className={`h-8 w-8 ${
                    canPreviousPage ? "text-primary-blue" : "text-gray-300"
                  }`}
                />
              </button>
            </li>
          )}
          {buttonsToShow &&
            buttonsToShow.map((b, index) => {
              return b === -1 ? (
                <button key={`b-${index}`}>{"..."}</button>
              ) : (
                <button
                  key={`b-${index}`}
                  className={`rounded-full h-10 w-10 flex justify-center items-center mx-1 ${
                    b - 1 === page
                      ? "bg-primary-blue text-white"
                      : "text-primary-blue"
                  }`}
                  onClick={(e) => {
                    jumpToPage(b - 1);
                  }}
                >
                  <div className="h-8 w-8 text-lg flex justify-center items-center">
                    {b}
                  </div>
                </button>
              );
            })}
          {showArrows && (
            <li>
              <button
                className={`rounded-full h-12 w-12 flex justify-center items-center mx-1 ${
                  canNextPage ? "" : "cursor-not-allowed"
                }`}
                onClick={(e) => {
                  nextPage();
                }}
              >
                <ChevronRightIcon
                  className={`h-8 w-8 ${
                    canNextPage ? "text-primary-blue" : "text-gray-300"
                  }`}
                />
              </button>
            </li>
          )}
          {showDoubleArrows && (
            <li>
              <button
                className={`rounded-full h-12 w-12 flex justify-center items-center mx-1 ${
                  canNextPage ? "" : "cursor-not-allowed"
                }`}
                onClick={(e) => {
                  jumpToEnd();
                }}
              >
                <ChevronDoubleRightIcon
                  className={`h-8 w-8 ${
                    canNextPage ? "text-primary-blue" : "text-gray-300"
                  }`}
                />
              </button>
            </li>
          )}
        </ul>
        <div className="hidden md:flex-grow"></div>
      </div>
    </div>
  );
};

export default Pager;
